import {
  SPLIT_SLEEP_DEFAULTS,
  type PhysiologicalDayResolution,
  type SleepEpisode,
} from './dailyOverrideTypes.ts'

const WEEKDAY_IDS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const

export type ProfileScheduleContext = {
  timezone: string
  circadian_phase: 'standard' | 'night_shift' | 'split_sleep'
  bio_window_hours: number
}

function durationMinutes(value: unknown): number | null {
  if (value == null) return null
  const n = Number(value)
  if (Number.isNaN(n)) return null
  return n > 1440 ? Math.round(n / 60) : Math.round(n)
}

export function parseSleepEpisode(
  data: Record<string, unknown>,
  provider: string,
): SleepEpisode | null {
  const startRaw = data.bedtime_start ?? data.bedtimeStart ?? data.start_time
  const endRaw = data.bedtime_stop ?? data.bedtime_end ?? data.bedtimeEnd ?? data.end_time
  if (!endRaw) return null

  const bedtime_start = String(startRaw ?? endRaw)
  const bedtime_end = String(endRaw)
  const startMs = Date.parse(bedtime_start)
  const endMs = Date.parse(bedtime_end)
  if (Number.isNaN(endMs)) return null

  let duration_minutes = durationMinutes(data.duration ?? data.total)
  if (duration_minutes == null && !Number.isNaN(startMs)) {
    duration_minutes = Math.max(0, Math.round((endMs - startMs) / 60_000))
  }
  if (duration_minutes == null || duration_minutes < SPLIT_SLEEP_DEFAULTS.nap_min_minutes) {
    return null
  }

  const is_nap = duration_minutes < SPLIT_SLEEP_DEFAULTS.main_sleep_min_minutes

  return {
    bedtime_start,
    bedtime_end,
    duration_minutes,
    is_nap,
    provider,
  }
}

export function localDateFromInstant(iso: string, timezone: string): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  return formatter.format(new Date(iso))
}

/** YYYY-MM-DD for "today" in the user's profile timezone. */
export function localTodayIso(timezone: string, reference = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(reference)
}

export function localHourFromInstant(iso: string, timezone: string): number {
  const hour = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: 'numeric',
    hour12: false,
  }).format(new Date(iso))
  return Number(hour)
}

function classifyCircadianPhase(
  anchorWakeAt: string,
  timezone: string,
  profilePhase: ProfileScheduleContext['circadian_phase'],
): 'standard' | 'night_shift' | 'split_sleep' {
  if (profilePhase === 'split_sleep') return 'split_sleep'
  const hour = localHourFromInstant(anchorWakeAt, timezone)
  if (profilePhase === 'night_shift' || (hour >= 14 && hour <= 23)) return 'night_shift'
  return 'standard'
}

function clusterEpisodes(episodes: SleepEpisode[], gapHours: number): SleepEpisode[][] {
  if (episodes.length === 0) return []
  const sorted = [...episodes].sort(
    (a, b) => Date.parse(a.bedtime_end) - Date.parse(b.bedtime_end),
  )
  const clusters: SleepEpisode[][] = [[sorted[0]]]
  for (let i = 1; i < sorted.length; i++) {
    const prev = clusters[clusters.length - 1]
    const lastEnd = Date.parse(prev[prev.length - 1].bedtime_end)
    const nextEnd = Date.parse(sorted[i].bedtime_end)
    const gapMs = nextEnd - lastEnd
    if (gapMs <= gapHours * 3_600_000) {
      prev.push(sorted[i])
    } else {
      clusters.push([sorted[i]])
    }
  }
  return clusters
}

function pickAnchorEpisode(episodes: SleepEpisode[]): SleepEpisode | null {
  const mains = episodes.filter((ep) => !ep.is_nap)
  const pool = mains.length > 0 ? mains : episodes
  return pool.reduce<SleepEpisode | null>((best, ep) => {
    if (!best) return ep
    return ep.duration_minutes > best.duration_minutes ? ep : best
  }, null)
}

export function computeExpiresAt(
  anchorWakeAt: string,
  timezone: string,
  circadianPhase: 'standard' | 'night_shift' | 'split_sleep',
  bioWindowHours: number,
): string {
  const anchor = new Date(anchorWakeAt)
  let expires = new Date(anchor.getTime() + bioWindowHours * 3_600_000)

  if (circadianPhase === 'standard') {
    const nextDay = localDateFromInstant(anchorWakeAt, timezone)
    const parts = nextDay.split('-').map(Number)
    const nextLocal = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2] + 1, 3, 0, 0))
    const standardExpiry = nextLocal.getTime()
    if (standardExpiry > anchor.getTime()) {
      expires = new Date(Math.min(expires.getTime(), standardExpiry))
    }
  }

  if (circadianPhase === 'night_shift') {
    const wakeDate = localDateFromInstant(anchorWakeAt, timezone)
    const [y, m, d] = wakeDate.split('-').map(Number)
    const noonNext = new Date(Date.UTC(y, m - 1, d + 1, 12, 0, 0))
    expires = new Date(Math.max(expires.getTime(), noonNext.getTime()))
  }

  return expires.toISOString()
}

export function resolvePhysiologicalDay(
  profile: ProfileScheduleContext,
  episodes: SleepEpisode[],
  clientHintDate?: string,
): PhysiologicalDayResolution {
  const timezone = profile.timezone || 'UTC'
  const clusters = clusterEpisodes(episodes, SPLIT_SLEEP_DEFAULTS.cluster_gap_hours)
  const latestCluster = clusters[clusters.length - 1] ?? []
  const anchor = pickAnchorEpisode(latestCluster)

  if (!anchor) {
    const fallbackDay = clientHintDate ?? localDateFromInstant(new Date().toISOString(), timezone)
    return {
      physiological_day: fallbackDay,
      timezone,
      circadian_phase: profile.circadian_phase,
      anchor_wake_at: null,
      sleep_episodes: episodes,
      is_main_sleep_ready: false,
    }
  }

  const circadian_phase = classifyCircadianPhase(anchor.bedtime_end, timezone, profile.circadian_phase)
  const physiological_day = localDateFromInstant(anchor.bedtime_end, timezone)
  const is_main_sleep_ready = !anchor.is_nap

  return {
    physiological_day,
    timezone,
    circadian_phase,
    anchor_wake_at: anchor.bedtime_end,
    sleep_episodes: latestCluster,
    is_main_sleep_ready,
  }
}

export function weekdayIdFromLocalDate(localDate: string, timezone: string): string {
  const [y, m, d] = localDate.split('-').map(Number)
  const noonUtc = new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'long',
  }).format(noonUtc)
  return weekday.toLowerCase()
}

export function buildVitalsSnapshot(
  episodes: SleepEpisode[],
  metrics: Record<string, unknown>,
): Record<string, unknown> {
  const main = pickAnchorEpisode(episodes.filter((ep) => !ep.is_nap)) ?? episodes[0]
  const napMinutes = episodes.filter((ep) => ep.is_nap).reduce((s, ep) => s + ep.duration_minutes, 0)
  const mainMinutes = main?.duration_minutes ?? 0
  const totalSleep = episodes.reduce((s, ep) => s + ep.duration_minutes, 0)

  return {
    ...metrics,
    main_sleep_minutes: mainMinutes,
    nap_sleep_minutes: napMinutes,
    total_sleep_minutes: totalSleep,
    anchor_wake_at: main?.bedtime_end ?? null,
    episode_count: episodes.length,
  }
}

export { WEEKDAY_IDS }
